// ** Redux Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { isNil } from 'lodash-es';
import { nanoid } from 'nanoid';

import { RootState } from '@/store';
import { createMlpProxyTransport } from '@/nova/utils/mlp-proxy';
import { getErrorMessage } from '@/utils/errors';

export const shuffleArray = <T>(array: T[] = []): T[] => {
  const newArray = [...array];

  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));

    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }

  return newArray;
};

const DEBUG = false;

type FetchDomainSuggestionsProps = {
  country: string;
};

type FetchDomainSuggestionsByDomainProps = {
  query: string;
  limit: number;
  country: string;
};

type Domain = {
  domain: string;
  available: boolean;
  price?: number;
  currency: string;
  period: number;
  error: any;
  reserved: boolean;
  unavailable: boolean;
  checked_at: string;
};

type Suggestion = {
  name: string;
  domains: Domain[];
  translation: string;
  why_it_works: string;
  target_segment: string;
  differentiation: string;
  concerns: string[];
  tier: number;
  positioning: string;
  country_code: string;
  rank?: number;
  created_at: string;
  updated_at: string;
};

export interface SimpleSuggestion {
  domain: string;
  available: boolean;
  price: number;
  currency: string;
  period: number;
  suggested_by_ai: boolean;
}

export interface FetchDomainSuggestionsByDomainResult {
  data: {
    success: boolean;
    message: string;
    suggestions: SimpleSuggestion[];
    query: string;
    timestamp: string;
  };
}

type FetchDomainSuggestionsResult = {
  data: {
    success: boolean;
    country_code: string;
    total_suggestions: number;
    available_domains_count: number;
    suggestions: Suggestion[];
    timestamp: string;
  };
};

type GenericError = {
  rejectValue: {
    error: string;
  };
};

const apiUrl = window.config.mlServiceApiUrl;

/**
 * The owner-hub session/utils art endpoints that MUTATE session art
 * (`/art/logo/upload`, `/art/logo/remove-background`) require the raw access token
 * in the `Authorization` header, exactly like the session slice's calls (no
 * "Bearer" prefix). The read-only suggestion / theme endpoints above are
 * session_id-gated only, so they don't send it. Returns an empty object when
 * there is no token so the request still goes out (and fails with the API's own
 * auth error) rather than throwing here.
 */
const authHeader = (state: RootState): Record<string, string> => {
  const token = state.authentication.access_token;
  return token ? { Authorization: token } : {};
};

export const fetchDomainsSuggestionsByCountry = createAsyncThunk<
  FetchDomainSuggestionsResult,
  FetchDomainSuggestionsProps,
  GenericError
>('fetchDomainsSuggestionsByCountry/query', async ({ country }, { getState, rejectWithValue }) => {
  try {
    const state: RootState = getState() as RootState;

    const sessionId = state.session.sessionId;

    const response: FetchDomainSuggestionsResult = await axios.get(
      `${apiUrl}/domains/suggestions/${country}/available`,
      {
        params: {
          session_id: sessionId,
        },
      },
    );

    if (response?.data?.success) {
      const selectedName = state.session.data?.customization_data?.brandName;

      const shuffled = shuffleArray(response.data.suggestions);

      if (!isNil(selectedName)) {
        const index = shuffled.findIndex((suggestion) => suggestion.name?.toLowerCase() === selectedName.toLowerCase());

        if (index > 0) {
          const [match] = shuffled.splice(index, 1);

          shuffled.unshift(match);
        }
      }

      response.data.suggestions = shuffled;

      return response;
    }

    return rejectWithValue({
      error: "Couldn't fetch domains suggestions",
    });
  } catch (error) {
    return rejectWithValue({
      error: getErrorMessage(error),
    });
  }
});

export const fetchDomainsSuggestionsByDomain = createAsyncThunk<
  FetchDomainSuggestionsByDomainResult,
  FetchDomainSuggestionsByDomainProps,
  GenericError
>('fetchDomainsSuggestionsByDomain/query', async ({ query, limit, country }, { getState, rejectWithValue }) => {
  try {
    const state: RootState = getState() as RootState;

    const sessionId = state.session.sessionId;

    const response: FetchDomainSuggestionsByDomainResult = await axios.post(
      `${apiUrl}/domains/godaddy/suggest`,
      {
        query,
        limit: limit || 10,
        country,
      },
      {
        params: {
          session_id: sessionId,
        },
      },
    );

    if (response?.data?.success) {
      // Preserve the API order: `suggest` returns domains pre-sorted by relevance
      // (exact brand + local TLD first, e.g. pokermaya.mx). The UI windows 3 at a
      // time and "Refresh" pages through them, so shuffling here buried the best
      // matches under random ones. Keep the backend's ranking intact.
      return response;
    }

    return rejectWithValue({
      error: "Couldn't fetch domains suggestions",
    });
  } catch (error) {
    return rejectWithValue({
      error: getErrorMessage(error),
    });
  }
});

/**
 * What spark-ai is told about the site a logo is for (its `SiteContext`): it seeds
 * the planner brief and the logo cache namespace on `/art/logo/generate`, and ranks
 * themes on `/art/themes`. Keys are omitted (not blank) when unknown.
 */
export type SiteContext = {
  /** Configurator art style name, e.g. "Premium Royal Neon Purple". */
  art_style?: string;
  /** Seed group name (the reference site look), e.g. "Stake". */
  seed_group?: string;
  /** Skin (palette) name, e.g. "stake-neon-indigo". */
  skin?: string;
  /** Operating-region code, e.g. "MEX" / "INT". */
  market?: string;
};

type FetchLogoProps = {
  brandName: string;
  primaryColor: string;
  secondaryColor: string;
  /** Free-form style phrase; superseded by `siteContext.art_style` when that is known. */
  artStyle?: string;
  background: string;
  theme: string;
  bypassCache: boolean;
  removeBackground: boolean;
  siteContext?: SiteContext;
};

type Image = {
  id: string;
  brand_name: string;
  cdn_url: string;
  theme: string; // rollbit
  generation_method: string; // theme-based (rollbit)
  width: number;
  height: number;
  format: string; // PNG
  status: string;
  primary_color: string;
  secondary_color: string;
  art_style: string;
  background: string;
  description: string;
  cache_hit: boolean;
  access_count: number;
};

type FetchLogoResult = {
  data: {
    // This is just an async request that completes right away, we will receive the result on websocket instead

    // success: boolean;
    // image_id: string;
    // image: Image;
    // elapsed_time: number;
    // cache_hit: boolean;

    request_id: string;
    status: string;
  };
};

const requestTokens: any = {};

export const fetchLogo = createAsyncThunk<FetchLogoResult, FetchLogoProps, GenericError>(
  'fetchLogo/query',
  async (
    {
      brandName,
      primaryColor,
      secondaryColor,
      artStyle = 'modern elegant',
      background,
      theme,
      bypassCache,
      removeBackground,
      siteContext,
    },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: RootState = getState() as RootState;

      const sessionId = state.session.sessionId;

      const cancelSource = axios.CancelToken.source();
      const requestId = nanoid(10);
      requestTokens[requestId] = cancelSource;

      const response: FetchLogoResult = await axios.post(
        `${apiUrl}/art/logo/generate`,
        {
          brand_name: brandName,
          primary_color: primaryColor,
          secondary_color: secondaryColor,
          // The site's real art style drives the prompt; the phrase is only a stand-in.
          art_style: siteContext?.art_style ?? artStyle,
          background,
          theme,
          site_context: siteContext,
          bypass_cache: bypassCache,
          remove_background: removeBackground,
          session_id: sessionId,
          request_id: requestId,
        },
        {
          params: {
            session_id: sessionId,
            request_id: requestId,
          },
          cancelToken: cancelSource.token,
        },
      );

      delete requestTokens[requestId];

      if (response?.data?.status === 'pending') {
        return response;
      }

      return rejectWithValue({
        error: "Couldn't fetch logo suggestion",
      });
    } catch (error) {
      return rejectWithValue({
        error: getErrorMessage(error),
      });
    }
  },
);

type FetchLogoThemesResult = {
  data: {
    success: boolean;
    themes: string[];
    all_options: string[];
    count: number;
    message: string;
  };
};

/**
 * spark-ai reached through the owner-hub MLP proxy (raw access token), not the
 * session-utils gateway (`mlServiceApiUrl`). The gateway registers spark-ai routes one
 * by one and forwards them with a bare URL, so it neither exposes `/art/logo/events`
 * nor passes the site-context query params `/art/themes` ranks with. `fetchLogo` stays
 * on the gateway: it owns the `request_id` → websocket callback contract and forwards
 * the JSON body verbatim.
 */
const art = createMlpProxyTransport({
  svc: 'api',
  scope: 'g',
  pathPrefix: '/api/v1',
  headers: { Accept: 'application/json' },
});

/** Themes best-fit-first for the described site; without a context, by engagement. */
export const fetchThemes = createAsyncThunk<FetchLogoThemesResult, SiteContext | undefined, GenericError>(
  'fetchThemes/query',
  async (siteContext, { rejectWithValue }) => {
    try {
      const response: FetchLogoThemesResult = await axios.get(
        art.url('/art/themes'),
        // A proxied 401 is spark-ai's answer, not an owner-hub session verdict: the
        // transport already opts out of the auth interceptor's refresh-and-teardown.
        art.requestConfig(undefined, siteContext),
      );

      if (response?.data?.success) {
        return response;
      }

      return rejectWithValue({
        error: "Couldn't fetch themes",
      });
    } catch (error) {
      return rejectWithValue({
        error: getErrorMessage(error),
      });
    }
  },
);

/**
 * Fire-and-forget engagement ping: tells the generation API which generated logo
 * the owner picked, so it can rank themes by real preference (the API resolves
 * the theme from the asset URL server-side). Goes through the MLP proxy (see `art`).
 * Deliberately swallowed: losing a ping must never affect the UX. No reducers listen.
 */
export const reportLogoSelected = createAsyncThunk<void, { cdnUrl: string }>(
  'reportLogoSelected/query',
  async ({ cdnUrl }) => {
    await axios
      .post(art.url('/art/logo/events'), { cdn_url: cdnUrl, event: 'selected' }, art.requestConfig())
      .catch(() => {});
  },
);

/**
 * Logo-art image returned by the upload / remove-background endpoints. Leaner
 * than the generated-logo `Image` (no theme / generation metadata): the only
 * field the UI consumes is `cdn_url`.
 */
type LogoArtImage = {
  id: string;
  brand_name: string | null;
  cdn_url: string;
  width: number;
  height: number;
  format: string;
  status: string;
};

type LogoArtResult = {
  data: {
    success: boolean;
    image_id: string;
    image: LogoArtImage;
    elapsed_time: string;
  };
};

type UploadLogoProps = {
  /** The image file the owner picked (PNG / SVG / JPEG). */
  file: File;
  /** Brand name stamped on the stored asset (optional server-side). */
  brandName: string;
  /**
   * Session whose art store receives the upload. Defaults to the active configurator
   * session (`state.session.sessionId`, the onboarding flow). Pass a launched brand's
   * deploy `session_id` (`deployments[0].session_id`) to upload against that brand from
   * outside onboarding, e.g. the Casino Settings "Replace logo" flow.
   */
  sessionId?: string;
};

/**
 * Upload an owner-supplied logo to the session's art store.
 *
 * `POST {mlServiceApiUrl}/art/logo/upload?session_id=<id>` — multipart body
 * (`logo` file + `brand_name`). `session_id` MUST travel as a QUERY param: the
 * session middleware authenticates before the multipart body is parsed, so a
 * body `session_id` is invisible to it (the API answers 401 "no session ID
 * provided"). Resolves to the stored asset; the CDN URL is `data.image.cdn_url`.
 * Pair with {@link removeLogoBackground} to strip the backing before use.
 */
export const uploadLogo = createAsyncThunk<LogoArtResult, UploadLogoProps, GenericError>(
  'mlServices/uploadLogo',
  async ({ file, brandName, sessionId: sessionIdOverride }, { getState, rejectWithValue }) => {
    try {
      const state: RootState = getState() as RootState;
      const sessionId = sessionIdOverride ?? state.session.sessionId;
      // Guard the (rare) lost-session race so we don't send `?session_id=undefined`
      // and surface the API's confusing 401 as if it were an auth failure.
      if (!sessionId) {
        return rejectWithValue({ error: 'No session found' });
      }

      const formData = new FormData();
      formData.append('logo', file);
      formData.append('brand_name', brandName);

      const response: LogoArtResult = await axios.post(`${apiUrl}/art/logo/upload`, formData, {
        // session_id is a query param on purpose — see the JSDoc above.
        params: { session_id: sessionId },
        headers: authHeader(state),
        // A 401 here is "this session can't accept uploads" (e.g. a launched brand's
        // terminal deploy session), NOT an auth-token expiry — so the global interceptor
        // must surface it as an upload failure, not log the user out. See auth-interceptor.
        _owsNoAuthTeardown: true,
      });

      if (response?.data?.success) {
        return response;
      }

      return rejectWithValue({
        error: "Couldn't upload logo",
      });
    } catch (error) {
      return rejectWithValue({
        error: getErrorMessage(error),
      });
    }
  },
);

type RemoveLogoBackgroundProps = {
  /** CDN URL of the source image (e.g. the one returned by {@link uploadLogo}). */
  imageUrl: string;
  /** Brand name stamped on the processed asset. */
  brandName: string;
  /** Output size; the onboarding flow uses the lighter `'preview'`. */
  size?: 'preview' | 'full';
  /** Session override; see {@link UploadLogoProps.sessionId}. Defaults to the active session. */
  sessionId?: string;
};

/**
 * Strip the background from a previously-stored logo.
 *
 * `POST {mlServiceApiUrl}/art/logo/remove-background` — JSON body
 * (`session_id`, `brand_name`, `image_url`, `size`). Unlike `art/logo/generate`
 * (which is async + websocket-streamed) this resolves SYNCHRONOUSLY (~2s) with
 * the processed asset; the transparent CDN URL is `data.image.cdn_url`.
 * `session_id` is sent both in the body (where the middleware reads it for JSON
 * requests) and as a query param, mirroring the other ml-services calls.
 */
export const removeLogoBackground = createAsyncThunk<LogoArtResult, RemoveLogoBackgroundProps, GenericError>(
  'mlServices/removeLogoBackground',
  async ({ imageUrl, brandName, size = 'preview', sessionId: sessionIdOverride }, { getState, rejectWithValue }) => {
    try {
      const state: RootState = getState() as RootState;
      const sessionId = sessionIdOverride ?? state.session.sessionId;
      if (!sessionId) {
        return rejectWithValue({ error: 'No session found' });
      }

      const response: LogoArtResult = await axios.post(
        `${apiUrl}/art/logo/remove-background`,
        {
          session_id: sessionId,
          brand_name: brandName,
          image_url: imageUrl,
          size,
        },
        {
          params: { session_id: sessionId },
          headers: authHeader(state),
          // See uploadLogo: a session-invalid 401 must not trigger the global auth teardown.
          _owsNoAuthTeardown: true,
        },
      );

      if (response?.data?.success) {
        return response;
      }

      return rejectWithValue({
        error: "Couldn't remove logo background",
      });
    } catch (error) {
      return rejectWithValue({
        error: getErrorMessage(error),
      });
    }
  },
);

export interface ConfigReducer {
  domainsSuggestionsByCountry: Suggestion[];
  domainsSuggestionsByDomain: SimpleSuggestion[];
  logosSuggestionsCount: number;
  logosSuggestions: {
    favicon: Image;
    logo: Image;
  }[];
  logoThemes: string[];
  logoOptions: string[];
  loading: boolean;
  loadingDomainsByCountry?: boolean;
  loadingDomainsByDomain?: boolean;
  loadingLogosCount: number;
  loadingLogos?: boolean;
  loadingThemes?: boolean;
  error: any;
}

export const mlServicesSlice = createSlice({
  name: 'mlServices',
  initialState: <ConfigReducer>{
    domainsSuggestionsByCountry: [],
    domainsSuggestionsByDomain: [],
    logosSuggestionsCount: 4,
    logosSuggestions: [],
    logoThemes: [],
    logoOptions: [],
    loading: false,
    loadingDomainsByCountry: false,
    loadingDomainsByDomain: false,
    loadingLogosCount: 0,
    loadingLogos: false,
    loadingThemes: false,
    error: null,
  },
  reducers: {
    setLogo: (state, action) => {
      // Clamp at 0: a superseded batch's requests are cancelled (their `rejected`
      // already decremented the count), but the server may still emit their
      // websocket results — so more `setLogo`s can arrive than are pending. Without
      // the clamp the count would go negative and never hit 0 (loading stuck on).
      state.loadingLogosCount = Math.max(0, state.loadingLogosCount - 1);

      if (state.loadingLogosCount === 0) {
        state.loading = false;
        state.loadingLogos = false;
      }

      if (action.payload?.data?.success) {
        const image = action.payload.data.image;
        // Dedupe by `cdn_url`: generation requests carry `remove_background: true`,
        // so the delivered asset is the server-cached transparent cutout — the SAME
        // url every time the same brand+theme combo resolves. The same image can
        // still arrive more than once — cache hits returning the same asset across
        // themes, or late websocket results from a superseded/cancelled batch —
        // which would otherwise render the same logo twice.
        const dedupeKey = image?.cdn_url;
        const exists =
          Boolean(dedupeKey) && state.logosSuggestions.some((entry) => entry.logo?.cdn_url === dedupeKey);
        if (!exists) {
          state.logosSuggestions.push({
            logo: image,
            favicon: image,
          });
        }
      }
    },
    clearDomainsSuggestionsByCountry: (state) => {
      state.domainsSuggestionsByCountry = [];
    },
    clearDomainsSuggestionsByDomain: (state) => {
      state.domainsSuggestionsByDomain = [];
    },
    clearLogosSuggestions: (state) => {
      state.logosSuggestions = [];

      Object.keys(requestTokens).forEach((key) => {
        const cancelSource = requestTokens[key];

        if (cancelSource) {
          cancelSource.cancel('Operation canceled by the user.');

          delete requestTokens[key];
        }
      });
    },
    clearState: (state) => {
      state.domainsSuggestionsByCountry = [];
      state.domainsSuggestionsByDomain = [];
      state.logosSuggestions = [];
      state.logoThemes = [];
      state.logoOptions = [];
      state.loading = false;
      state.loadingDomainsByCountry = false;
      state.loadingDomainsByDomain = false;
      state.loadingLogos = false;
      state.loadingThemes = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(fetchDomainsSuggestionsByCountry.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingDomainsByCountry = false;
        if (action.payload?.data?.success) {
          state.domainsSuggestionsByCountry = action.payload.data.suggestions;
        }
        DEBUG && console.timeEnd('fetchDomainsSuggestionsByCountry.query');
        DEBUG && console.log('fetchDomainsSuggestionsByCountry.fulfilled', action.payload);
      })
      .addCase(fetchDomainsSuggestionsByCountry.pending, (state) => {
        DEBUG && console.time('fetchDomainsSuggestionsByCountry.query');
        state.loading = true;
        state.loadingDomainsByCountry = true;
      })
      .addCase(fetchDomainsSuggestionsByCountry.rejected, (state, action) => {
        state.loading = false;
        state.loadingDomainsByCountry = false;
        state.error = action.payload?.error;
        DEBUG && console.log('fetchDomainsSuggestionsByCountry.rejected', action.payload);
      })

      .addCase(fetchDomainsSuggestionsByDomain.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingDomainsByDomain = false;
        if (action.payload?.data?.success) {
          state.domainsSuggestionsByDomain = action.payload.data.suggestions;
        }
        DEBUG && console.timeEnd('fetchDomainsSuggestionsByDomain.query');
        DEBUG && console.log('fetchDomainsSuggestionsByDomain.fulfilled', action.payload);
      })
      .addCase(fetchDomainsSuggestionsByDomain.pending, (state) => {
        DEBUG && console.time('fetchDomainsSuggestionsByDomain.query');
        state.loading = true;
        state.loadingDomainsByDomain = true;
      })
      .addCase(fetchDomainsSuggestionsByDomain.rejected, (state, action) => {
        state.loading = false;
        state.loadingDomainsByDomain = false;
        state.error = action.payload?.error;
        DEBUG && console.log('fetchDomainsSuggestionsByDomain.rejected', action.payload);
      })

      .addCase(fetchLogo.fulfilled, (state, action) => {
        // This is just an async request that completes right away, we will receive the result on websocket instead

        // state.loadingLogosCount = state.loadingLogosCount - 1;
        // if (state.loadingLogosCount === 0) {
        //   state.loading = false;
        //   state.loadingLogos = false;
        // }
        // if (action.payload?.data?.success) {
        //   const img = action.payload.data.image;
        //   state.logosSuggestions.push({
        //     logo: img,
        //     favicon: img,
        //   });
        // }

        DEBUG && console.timeEnd('fetchLogo.query');
        DEBUG && console.log('fetchLogo.fulfilled', action.payload);
      })
      .addCase(fetchLogo.pending, (state) => {
        DEBUG && console.time('fetchLogo.query');
        state.loadingLogosCount = state.loadingLogosCount + 1;
        state.loading = true;
        state.loadingLogos = true;
      })
      .addCase(fetchLogo.rejected, (state, action) => {
        state.loadingLogosCount = state.loadingLogosCount - 1;
        if (state.loadingLogosCount === 0) {
          state.loading = false;
          state.loadingLogos = false;
        }
        state.error = action.payload?.error;
        DEBUG && console.log('fetchLogo.rejected', action.payload);
      })

      .addCase(fetchThemes.fulfilled, (state, action) => {
        state.loading = false;
        state.loadingThemes = false;
        if (action.payload?.data?.success) {
          state.logoThemes = action.payload.data.themes;
          state.logoOptions = action.payload.data.all_options;
        }
        DEBUG && console.timeEnd('fetchThemes.query');
        DEBUG && console.log('fetchThemes.fulfilled', action.payload);
      })
      .addCase(fetchThemes.pending, (state) => {
        DEBUG && console.time('fetchThemes.query');
        state.loading = true;
        state.loadingThemes = true;
      })
      .addCase(fetchThemes.rejected, (state, action) => {
        state.loading = false;
        state.loadingThemes = false;
        state.error = action.payload?.error;
        DEBUG && console.log('fetchThemes.rejected', action.payload);
      });
  },
});

export const {
  clearDomainsSuggestionsByCountry,
  clearDomainsSuggestionsByDomain,
  clearLogosSuggestions,
  clearState,
  setLogo,
} = mlServicesSlice.actions;


export default mlServicesSlice.reducer;
